import { useState } from "react";
import { Mail, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact newsletter signup form. Drop in anywhere (footer, sidebar, etc.).
 * @param {Object} props
 * @param {string} [props.source="footer"] - tracks WHERE the user subscribed from
 * @param {string} [props.title] - optional heading override
 */
export default function NewsletterSignup({ source = "footer", title = "Rejoins la newsletter KAZAJAH" }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true);
  const [status, setStatus] = useState("idle"); // idle | loading | success | already | error
  const [errorMsg, setErrorMsg] = useState("");

  const isValid = /\S+@\S+\.\S+/.test(email) && consent;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), consent, source }),
      });
      const data = await res.json();
      if (!res.ok) {
        setStatus("error");
        setErrorMsg(data?.detail || "Inscription impossible");
        return;
      }
      setStatus(data.status === "already_subscribed" ? "already" : "success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Erreur réseau");
    }
  };

  return (
    <form data-testid="newsletter-form" onSubmit={handleSubmit} className="w-full max-w-md">
      <div className="flex items-center gap-2 mb-2">
        <Mail size={14} className="text-amber-400" />
        <p className="text-xs text-zinc-400 uppercase tracking-[0.2em]">{title}</p>
      </div>

      {status === "success" || status === "already" ? (
        <div data-testid="newsletter-success"
          className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm">
          <Check size={16} />
          {status === "success" ? "Bienvenue ! On a envoyé un email de confirmation." : "Tu es déjà dans la liste — merci pour ton soutien !"}
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="newsletter-email"
              type="email"
              required
              placeholder="ton.email@exemple.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === "loading"}
              className="flex-1 bg-zinc-900 border border-zinc-800 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-amber-400 focus:outline-none"
            />
            <button
              data-testid="newsletter-submit"
              type="submit"
              disabled={!isValid || status === "loading"}
              className={`px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${isValid && status !== "loading" ? "bg-amber-400 text-black hover:bg-amber-300" : "bg-zinc-800 text-zinc-600 cursor-not-allowed"}`}>
              {status === "loading" ? <Loader2 size={16} className="animate-spin" /> : "M'inscrire"}
            </button>
          </div>
          <label className="flex items-start gap-2 mt-2 text-[11px] text-zinc-500 cursor-pointer select-none">
            <input data-testid="newsletter-consent" type="checkbox" checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 accent-amber-400" />
            <span>J'accepte de recevoir les actus du label (sorties, événements). Désinscription possible à tout moment.</span>
          </label>
          {status === "error" && (
            <p data-testid="newsletter-error" className="text-rose-400 text-xs mt-2">{errorMsg}</p>
          )}
        </>
      )}
    </form>
  );
}
